/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { SandboxedModule, Telemetry, } from "@breadboard-ai/jsandbox";
import { inflateData } from "./data/inflate-deflate.js";
import { CapabilitiesManagerImpl } from "./sandbox/capabilities-manager.js";
export { addSandboxedRunModule };
function findHandler(handlerName, kits) {
    const handler = kits
        ?.flatMap((kit) => Object.entries(kit.handlers))
        .find(([name]) => name === handlerName)
        ?.at(1);
    return handler;
}
function addSandboxedRunModule(sandbox, kits) {
    const existingRunModule = findHandler("runModule", kits);
    const originalDescriber = (existingRunModule &&
        typeof existingRunModule !== "string" &&
        "describe" in existingRunModule
        ? existingRunModule.describe
        : undefined) ??
        (() => ({
            outputSchema: {},
            inputSchema: {},
        }));
    return [
        {
            url: import.meta.url,
            handlers: {
                runModule: {
                    invoke: async ({ $module, ...rest }, context) => {
                        const moduleDeclaration = context.outerGraph?.modules;
                        if (!moduleDeclaration) {
                            return {
                                $error: `Unable to run module: no modules found within board ${context.board?.url || "uknown board"}`,
                            };
                        }
                        const telemetry = Telemetry.create(context);
                        const modules = Object.fromEntries(Object.entries(moduleDeclaration).map(([name, spec]) => [
                            name,
                            spec.code,
                        ]));
                        if (context.graphStore) {
                            const { graphStore } = context;
                            const mainGraphId = graphStore.getByDescriptor(context.outerGraph);
                            if (mainGraphId.success) {
                                const mutable = await graphStore.getLatest(graphStore.get(mainGraphId.result));
                                await addImportedModules(modules, mutable);
                            }
                        }
                        const module = new SandboxedModule(sandbox, new CapabilitiesManagerImpl(context).createSpec(), modules);
                        const inputs = context.store
                            ? (await inflateData(context.store, rest, context.base))
                            : rest;
                        const result = await module.invoke($module, inputs, telemetry);
                        return result;
                    },
                    describe: async (inputs, inputSchema, outputSchema, 
                    /**
                     * The context in which the node is described.
                     */
                    context) => {
                        const { $module } = inputs || {};
                        const moduleDeclaration = context?.outerGraph?.modules;
                        if ($module && moduleDeclaration) {
                            const modules = Object.fromEntries(Object.entries(moduleDeclaration).map(([name, spec]) => [
                                name,
                                spec.code,
                            ]));
                            const module = new SandboxedModule(sandbox, {}, modules);
                            try {
                                const result = (await module.describe($module, {
                                    inputs,
                                    inputSchema,
                                    outputSchema,
                                }));
                                return {
                                    inputSchema: {
                                        type: "object",
                                        properties: {
                                            $module: {
                                                type: "string",
                                                title: "Module ID",
                                                behavior: ["config", "module"],
                                            },
                                            ...result.inputSchema.properties,
                                        },
                                    },
                                    outputSchema: result.outputSchema,
                                };
                            }
                            catch (e) {
                                // swallow the error. It's okay that some modules don't have
                                // custom describers.
                            }
                        }
                        return originalDescriber(inputs, inputSchema, outputSchema, context);
                    },
                },
            },
        },
        ...kits,
    ];
}
async function addImportedModules(modules, mutable) {
    const inspectable = mutable.graphs.get("");
    if (!inspectable)
        return;
    const imports = await inspectable.imports();
    imports.forEach((imported, importName) => {
        if ("$error" in imported)
            return;
        for (const [moduleName, spec] of Object.entries(imported.modules())) {
            const modulePath = `${importName}/${moduleName}`;
            modules[modulePath] = spec.code();
        }
    });
}
//# sourceMappingURL=sandboxed-run-module.js.map